import { ActionsDispatcher } from 'store/ActionsDispatcher';
import { buildLogger } from '@instinet-analytics/core-lib';
import { PortfolioService } from 'portfolio/PortfolioService';
import { WidgetsService } from 'widgets/WidgetsService';
import { selectActiveWidgetIds } from 'layout/store/selectors';
import { WidgetCustomStreamManagerSupportingRegisteredSymbolsBase } from './WidgetCustomStreamManagerSupportingRegisteredSymbols';
import uniq from 'lodash/uniq';
import without from 'lodash/without';
import { MessageData } from '@instinet-analytics/core-lib';
import {
    StreamRequestInfo,
    WidgetCustomManagedOptions,
} from './WidgetCustomStreamManager';
import { MarketDataRequest } from 'marketData/MarketDataRequest';
import { WidgetMarketDataRequestManager } from 'marketData/WidgetMarketDataRequestManager';
import { DataServices } from 'store/Services';
import flatten from 'lodash/flatten';
import { ThrottledPromises } from 'utils/ThrottledPromises';

type WidgetStreamByRequestUidStreamManagerOptions =
    WidgetCustomManagedOptions & {
        checkOrderIds?: boolean;
        marketDataRequestManager: WidgetMarketDataRequestManager;
    };

export abstract class WidgetStreamByRequestUidStreamManagerBase extends WidgetCustomStreamManagerSupportingRegisteredSymbolsBase {
    protected readonly portfolioService: PortfolioService;
    private readonly widgetsService: WidgetsService;
    private readonly checkOrderIds: boolean;
    private readonly marketDataRequestManager: WidgetMarketDataRequestManager;
    private readonly dataServices: DataServices;
    protected streamKeys: string[] = [];
    protected readonly uuid: string;
    private throttledCheckStream = new ThrottledPromises();

    private currentStreamRequestInfos: StreamRequestInfo<unknown>[] = [];

    public constructor(
        logSource: string,
        uuid: string,
        options: WidgetStreamByRequestUidStreamManagerOptions
    ) {
        super(options.store, buildLogger(logSource, options.loggingService));
        this.marketDataRequestManager = options.marketDataRequestManager;
        this.portfolioService = options.portfolioService;
        this.widgetsService = options.widgetsService;
        this.dataServices = options.dataServices;
        this.uuid = uuid;

        this.checkOrderIds = !!options.checkOrderIds;
    }

    public checkStream(reason: string, dispatcher: ActionsDispatcher): void {
        const queuedCallback = () =>
            this.checkStreamAsync(reason, dispatcher).then(() => {
                dispatcher.dispose();
            });
        this.throttledCheckStream.queue(queuedCallback.bind(this));
    }

    private async checkStreamAsync(
        reason: string,
        dispatcher: ActionsDispatcher
    ): Promise<void> {
        this.log('Checking for ' + reason);
        const result = await this.getStreamRequestInfos();

        const { streamRequests: nextStreamRequestInfos, marketDataRequests } =
            result;

        await this.marketDataRequestManager.register(
            this.uuid,
            marketDataRequests
        );

        const nextStreamKeys = uniq(
            nextStreamRequestInfos.map((s) => s.streamKey)
        );
        const currentStreamKeys = this.currentStreamRequestInfos.map(
            (i) => i.streamKey
        );

        const addingStreamKeys = without(nextStreamKeys, ...currentStreamKeys);
        const removingStreamKeys = without(
            currentStreamKeys,
            ...nextStreamKeys
        );
        const updatingStreamKeys = uniq(
            nextStreamRequestInfos
                .filter((n) =>
                    this.currentStreamRequestInfos.find(
                        (c) =>
                            c.streamKey === n.streamKey &&
                            c.payloadUid !== n.payloadUid
                    )
                )
                .map((s) => s.streamKey)
        );

        let staleStreamKeys: string[] = [];
        if (removingStreamKeys.length > 0) {
            staleStreamKeys = this.clearAndRemoveStaleStreamData(
                dispatcher,
                addingStreamKeys,
                removingStreamKeys,
                nextStreamKeys
            );
        }

        this.logger.debug('Checked', {
            current: currentStreamKeys,
            adding: addingStreamKeys,
            removing: removingStreamKeys,
            stale: staleStreamKeys,
            updating: updatingStreamKeys,
            next: nextStreamKeys,
        });

        for (const startingStreamKey of [
            ...addingStreamKeys,
            ...updatingStreamKeys,
        ]) {
            const startingRequest = nextStreamRequestInfos.find(
                (s) => s.streamKey === startingStreamKey
            )!;
            this.addStreamKey(startingRequest.streamKey);
            await this.startStream(startingRequest, dispatcher, reason);
        }
        for (const removingStreamKey of uniq(removingStreamKeys)) {
            this.stopStream(removingStreamKey, dispatcher, reason);
        }

        this.logger.debug(
            'Setting next stream request infos',
            nextStreamRequestInfos
        );
        this.currentStreamRequestInfos = nextStreamRequestInfos;
    }

    public getInfo(): Record<string, unknown> {
        return {
            type: 'WidgetStreamByRequestUidStreamManager',
            registeredSymbolStreams: this.registeredSymbolStreams,
            widgetIds: this.widgetIds,
            streamKeys: this.currentStreamRequestInfos.map(
                (i) => `${i.streamKey}${i.payloadUid ? '-' + i.payloadUid : ''}`
            ),
        };
    }

    private async getStreamRequestInfos(): Promise<{
        streamRequests: StreamRequestInfo<unknown>[];
        marketDataRequests: MarketDataRequest[];
    }> {
        const state = this.store.getState();
        const activeWidgetIds = selectActiveWidgetIds(state);
        const activeRegisteredWidgetIds = this.widgetIds.filter(
            (a) => activeWidgetIds.indexOf(a.widgetKey) > -1
        );

        const activeInputUsesOrdersStream =
            this.portfolioService.activeInputUsesOrdersStream;

        const widgetStreamKeyPromises = activeRegisteredWidgetIds.map((s) =>
            this.widgetsService.getRequestsWithRequestUid(
                this.dataServices,
                s,
                activeInputUsesOrdersStream
            )
        );
        const widgetStreamKeyResults = await Promise.all(
            widgetStreamKeyPromises
        );
        const widgetStreamKeys: StreamRequestInfo<unknown>[] = [];
        for (const widgetStreamKeyResult of widgetStreamKeyResults) {
            if (Array.isArray(widgetStreamKeyResult)) {
                widgetStreamKeys.push(...widgetStreamKeyResult);
            } else if (widgetStreamKeyResult) {
                widgetStreamKeys.push(widgetStreamKeyResult);
            }
        }

        const marketDataRequestPromises = activeRegisteredWidgetIds.map((s) =>
            this.widgetsService.getMarketDataRequests(
                this.dataServices,
                s,
                activeInputUsesOrdersStream
            )
        );

        const marketDataRequestsResolved = await Promise.all(
            marketDataRequestPromises
        );

        const marketDataRequests = flatten(marketDataRequestsResolved);

        const activeRegisteredSymbolStreamWidgetIds = Object.keys(
            this.registeredSymbolStreams
        ).filter((wk) => activeWidgetIds.indexOf(wk) > -1);

        const rssPromises = activeRegisteredSymbolStreamWidgetIds.map((s) =>
            this.widgetsService.getRequestsByRequestUidForRegisteredSymbolStreams(
                this.dataServices,
                this.registeredSymbolStreams[s]
            )
        );
        const rssKeys = await Promise.all(rssPromises);
        widgetStreamKeys.push(...flatten(rssKeys));

        const mktPromises = activeRegisteredSymbolStreamWidgetIds.map((s) =>
            this.widgetsService.getMarketDataRequestsForRegisteredSymbolStreams(
                this.dataServices,
                this.registeredSymbolStreams[s]
            )
        );
        const mktPromisesResolved = await Promise.all(mktPromises);
        marketDataRequests.push(...flatten(mktPromisesResolved));

        const streamRequests = widgetStreamKeys.filter(
            (w) => !!w
        ) as StreamRequestInfo<unknown>[];

        return { streamRequests, marketDataRequests };
    }

    protected abstract startStream(
        requestInfo: StreamRequestInfo<unknown>,
        dispatcher: ActionsDispatcher,
        reason: string
    ): Promise<void>;

    protected abstract stopStream(
        streamKey: string,
        dispatcher: ActionsDispatcher,
        reason: string
    ): void;

    protected log(message: string, reason?: string, data?: MessageData) {
        const reasonText = reason ? `(${reason})` : '';
        this.logger.debug(`${message} - ${reasonText}`, data);
    }

    public onPortfolioStreamingOrderIdsChanged(
        dispatcher: ActionsDispatcher
    ): void {
        if (this.checkOrderIds) {
            this.checkStream('onPortfolioStreamingOrderIdsChanged', dispatcher);
        }
    }

    private addStreamKey(streamKey: string) {
        if (!this.streamKeys.includes(streamKey)) {
            this.streamKeys.push(streamKey);
        }
    }

    protected abstract clearAndRemoveStaleStreamData(
        dispatcher: ActionsDispatcher,
        adding: string[],
        removing: string[],
        next: string[]
    ): string[];

    protected clearResponse(streamKey: string) {
        this.currentStreamRequestInfos = this.currentStreamRequestInfos.filter(
            (k) => k.streamKey !== streamKey
        );
    }

    public resetState() {
        this.currentStreamRequestInfos = [];
    }
}
